import { useState, useEffect, useCallback } from 'react';
import type { CRMField } from '@/types/saas';
import { useSaaSModule } from './use-saas-module';
import { useSettingsStore } from '@/stores/settings-store';

/**
 * Hook for CRM field mapping management
 */
export function useFieldMapping(crmModule?: string) {
  const [crmFields, setCrmFields] = useState<CRMField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { module: saasModule } = useSaaSModule();
  const fieldMappings = useSettingsStore((s) => s.fieldMappings);

  const fetchCRMFields = useCallback(async () => {
    if (!saasModule || !crmModule) return;

    setLoading(true);
    setError(null);
    try {
      // Prefer getLookupModuleFields when the module exposes it (HubSpot).
      // Mirrors the original app where the field-mapping editor reads from
      // LOOKUP_SUPPORTED_MODULES[module].fields (rich set with owners /
      // pipelines / pipeline stages / enumerations + picklist values),
      // not from the narrow template-variable dropdown set.
      let fields: CRMField[];
      if (saasModule.getLookupModuleFields) {
        const map = await saasModule.getLookupModuleFields(crmModule);
        fields = Object.values(map);
      } else {
        fields = await saasModule.getModuleFields(crmModule);
      }
      setCrmFields(fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CRM fields');
    } finally {
      setLoading(false);
    }
  }, [saasModule, crmModule]);

  useEffect(() => {
    fetchCRMFields();
  }, [fetchCRMFields]);

  const supportedModules = saasModule?.getSupportedModules() || [];

  return {
    crmFields,
    fieldMappings,
    supportedModules,
    loading,
    error,
    refresh: fetchCRMFields,
  };
}
